/**
 * clipLook.js — every number that decides how the clips LOOK, in one object.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THIS FILE EXISTS TO BE EDITED DURING A TUNING PASS. Ben is judging the
 * flash on a cast TV across a room, which is the only place the answer lives.
 * Every value below is a dial he will want to try two or three settings of, so
 * they are here together rather than scattered through the components — one
 * file open, save, look at the TV, save again.
 *
 * 🔴 NOTHING HERE CHANGES BEHAVIOUR, ONLY APPEARANCE. The kill switch, the
 * trigger, the input guarantees and the /watch gate are all elsewhere and none
 * of them read this file. A bad value here makes the flash ugly; it cannot make
 * it fire at the wrong time or eat a keystroke.
 *
 * WHY THE FLASH IS BLURRED AND THE CARD IS NOT. The clips are ~480px wide. The
 * card shows one at ~600px, which is close to native. The flash puts the same
 * source across a 1920px page, so the grain is real and unavoidable — blur is
 * the strongest lever against it, and it also does the more important job of
 * making the clip read as ATMOSPHERE rather than as video somebody is supposed
 * to watch while bidding.
 * ══════════════════════════════════════════════════════════════════════
 */
export const CLIP_LOOK = {
  // ── THE FULL-PAGE FLASH ────────────────────────────────────────────
  /**
   * Peak opacity of the whole beat — the single intensity dial. The fade
   * animates the layer from 0 to this and back, so raising it makes everything
   * (footage and vignette together) stronger without changing the balance.
   */
  flashOpacity: 0.22,

  /** Blur on the flash footage. The grain lever. 0 disables it. */
  flashBlurPx: 3,

  /**
   * The beat, in three parts. The entrance is deliberately not instant — the
   * clip arriving at full strength is what read as "too hard".
   *
   * 🔴 THE HOLD IS NOW A FLOOR, NOT THE DURATION. Ben: "I want the whole clip to
   * play." The hold is measured off the video itself (`loadedmetadata` ->
   * `video.duration`) and clamped between these two numbers, so the beat ends
   * when the clip ends instead of at a constant guessing a length that varies
   * per file. Every clip in the set is longer than 1400ms, so the shipped beat
   * was cutting every single one off mid-play.
   *
   * `flashHoldMs` is the FALLBACK as well as the floor: a duration that never
   * resolves — a slow phone, a 404 on the id, a browser that will not report it
   * — falls back to exactly the behaviour that shipped.
   */
  flashFadeInMs: 400,
  flashHoldMs: 1400,
  flashFadeOutMs: 700,

  /**
   * The ceiling on a measured hold.
   *
   * 🔴 A DIAL, NOT A MAGIC NUMBER IN A COMPONENT, and it exists because some
   * clip in a 46-file set will be long. The beat sits over the board while Ben
   * is typing a price, so "however long the file is" is not an acceptable
   * answer — anything past this gets cut, which is the shipped behaviour applied
   * to the tail instead of to every clip.
   *
   * ⚠️ Ben's call once he has seen it on the TV. 6s is a starting point.
   *
   * MEASURED (docs/reference/clip-bandwidth-aug12.md is the sibling number):
   * the clips are highlight loops, typically 2-5s, so this bites rarely.
   */
  flashMaxHoldMs: 6000,

  /**
   * Darkens the edges so the footage sits in the page rather than on top of it.
   * ⚠️ Read as a string, never parsed — jsdom drops gradients from inline
   * styles, so tests pin this value here rather than off the element.
   */
  flashVignette:
    "radial-gradient(ellipse at center, rgba(10,15,26,0) 30%, rgba(10,15,26,0.55) 75%, rgba(10,15,26,0.9) 100%)",

  // ── THE /rate TEAM REPORT'S HERO CYCLE ──────────────────
  /**
   * How long one player holds the hero backdrop before the next rostered
   * player with a clip takes it, in seconds.
   *
   * Ben: "show more clips from the other players on the squad, they should
   * cycle." A single clip on a report somebody reads for a minute is a loop
   * they stop seeing; the cycle is what makes the backdrop read as THIS TEAM
   * rather than as one random highlight.
   *
   * 🔴 A DIAL, BECAUSE THE RIGHT VALUE IS A JUDGEMENT ABOUT READING SPEED.
   * Too short and the card flickers under someone studying a roster; too long
   * and a reader never sees the second player. 8s is a starting point and this
   * file exists so it can be tried at 6 and at 12 without touching a component.
   */
  heroCycleSeconds: 8,

  /**
   * The fade at EACH END of a swap, in ms.
   *
   * 🔴 ONE VIDEO IS STILL THE RULE, so this is a dissolve through the card
   * rather than a true cross-dissolve between two clips: the backdrop fades to
   * nothing over this long, swaps its source, and fades back over this long
   * again. Mounting a second video to overlap them would double the bandwidth
   * and break the "at most one video on this page, ever" guarantee the report
   * is built on, which is worth more than the prettier transition.
   *
   * The caption fades with the footage, so the name on screen and the player on
   * screen change in the same instant.
   */
  heroCrossfadeMs: 600,

  // ── SHARED BY THE CARD AND THE FLASH ───────────────────────────────
  /**
   * 🔴 THE DEFAULT CROP, AND IT IS NOT `center`.
   *
   * Highlight clips frame a whole body; the interesting part is the top third.
   * Centre-cropping a 116px-tall card or a letterboxed page put chests on
   * screen. Every clip without its own `focus` gets this.
   *
   * ⚠️ IT IS A REAL VALUE, NOT THE CSS DEFAULT. Before Aug 11 2026 a focus-less
   * entry emitted no `object-position` at all, and a test asserted that its
   * markup matched the card that shipped before the field existed. That
   * reference is now "a card carrying this default" — see the note on that test
   * in nominationClip.test.js. An entry's own `focus` still overrides it.
   */
  defaultFocus: "center 30%",
};

export default CLIP_LOOK;
